// Story 1.27 — TanStack Query hook wrapping GET /api/v1/account/me.
//
// First in-SPA caller of /api/v1/account/me. The Astro public-site
// has its own one-shot detector (apps/public-site/src/lib/session-
// detector.ts) but the SPA wants cache + invalidation, so we use
// TanStack Query here. The shape mirrors the backend response from
// routes/account.rs.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type AccountMe = {
  user_id: string;
  email: string;
  creator_status: "none" | "applied" | "approved" | "rejected" | "suspended";
  is_admin: boolean;
  /** Story 3.7 — user-level animation preference driving
   *  useEffectiveMotionMode() resolution. `auto` (default) mirrors the
   *  OS prefers-reduced-motion media query; `reduced` forces the
   *  reduced-motion variant; `full` overrides the OS preference. */
  animation_preference: "auto" | "reduced" | "full";
  /** Story 3.8 — denormalized counter incremented by the reveal
   *  transaction (Story 3.2 reveal_orchestrator.rs). useEffectiveCeremonyMode
   *  reads this to gate the visibility of the skip-ceremony-after-
   *  graduation toggle on /account/preferences per UX-DR33
   *  (>= 10 unlocks the toggle + the quick-reveal mode). */
  dossiers_opened_count: number;
  /** Story 3.8 — opt-in power-user graduation toggle. When true AND
   *  dossiers_opened_count >= 10 AND share.tier !== "founders",
   *  useEffectiveCeremonyMode resolves to "quick-reveal" per UX-DR33
   *  + the Founder's exception override (UX spec L612). */
  skip_ceremony_after_graduation: boolean;
  /** Story 4.5 — user handle (kebab-case, unique). NULL when the
   *  user hasn't yet claimed one. AccountProfilePage drives the
   *  claim CTA off this. */
  handle: string | null;
  /** Story 4.5 — public-profile bio (≤500 chars). NULL when unset. */
  public_bio: string | null;
  /** Story 4.5 — profile visibility enum. Defaults to `"public"`
   *  per migration 0035. */
  profile_visibility: "public" | "private";
  /** Set at creator approval and never cleared. A consent withdrawal
   *  flips creator_status back to "none" but this stays — marking a
   *  FORMER creator, who keeps read access to the creator pages
   *  (stats + withdrawing from remaining portfolios). */
  creator_approved_at: string | null;
  /** 2FA rework (migration 0072) — user has TOTP enrolled. */
  totp_enrolled: boolean;
  /** 2FA rework (migration 0072) — THIS session proved the second factor
   *  (login verify or enrollment confirm). Admin surface requires it;
   *  AdminGuard offers a step-up code prompt when it is false. */
  session_totp_verified: boolean;
};

export type AccountMeEnvelope = { data: AccountMe };

/** Approved creators AND former creators (withdrew consent) may view
 *  the creator surfaces. Mirrors the server's history-read gate. */
export function hasCreatorPageAccess(me: AccountMe | undefined): boolean {
  if (!me) return false;
  // `!= null` (loose) on purpose: treats a missing field (old cached
  // payload, test stub) the same as null — no access granted by absence.
  return (
    me.creator_status === "approved" ||
    (me.creator_status === "none" && me.creator_approved_at != null)
  );
}

/** Former creator: once approved, since withdrew consent. */
export function isFormerCreator(me: AccountMe | undefined): boolean {
  return me?.creator_status === "none" && me.creator_approved_at != null;
}

export type AccountMeFetchError = {
  code: string;
  message: string;
  status: number;
};

async function fetchAccountMe(): Promise<AccountMeEnvelope> {
  const response = await fetch("/api/v1/account/me", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: AccountMeFetchError = {
      code: "ACCOUNT_ME_FETCH_FAILED",
      message: `Session fetch failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as AccountMeEnvelope;
}

export function useAccountMe() {
  return useQuery<AccountMeEnvelope, AccountMeFetchError>({
    queryKey: ["account-me"],
    queryFn: fetchAccountMe,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
